import { getContrastingColor, hasMinimumContrast } from '../utils/colorContrast';

import { BeehiivLogo } from './BeehiivLogo';
import {
  absoluteWrapper,
  container,
  copyrightSection,
  copyrightText,
  linksSection,
  policyLink,
  policyLinks,
  poweredByBadge,
  poweredByLogo,
} from './ProprietaryFooter.css';
import type {
  ProprietaryFooterData,
  ProprietaryFooterElementProps,
  ProprietaryFooterProps,
} from './ProprietaryFooter.type';

interface PolicyLinkProps {
  href: string;
  text: string;
  color: string;
  publicationName?: string;
}

const PolicyLink = ({ href, text, color, publicationName }: PolicyLinkProps) => {
  try {
    const url = new URL(href);

    if (publicationName) {
      url.searchParams.append('utm_source', publicationName.toLowerCase().replace(/\s+/g, '_'));
    }

    return (
      <a href={url.toString()} target="_blank" rel="noreferrer" className={policyLink} style={{ color }}>
        {text}
      </a>
    );
  } catch {
    // Fallback to href as-is if URL construction fails (invalid/relative URLs)
    return (
      <a href={href} target="_blank" rel="noreferrer" className={policyLink} style={{ color }}>
        {text}
      </a>
    );
  }
};

interface PoweredByBadgeProps {
  publicationName: string;
  text: string;
  color: string;
}

const PoweredByBadge = ({ publicationName, text, color }: PoweredByBadgeProps) => {
  const utmSource = publicationName.toLowerCase().replace(/\s+/g, '_');

  return (
    <a
      target="_blank"
      rel="noreferrer"
      href={`https://www.beehiiv.com/?utm_source=${utmSource}&utm_medium=footer`}
      className={poweredByBadge}
      style={{
        borderColor: color,
        color,
      }}
    >
      <BeehiivLogo className={poweredByLogo} />
      <span>{text}</span>
    </a>
  );
};

interface ProprietaryFooterCoreProps {
  backgroundColor?: string;
  textColor?: string;
  data: ProprietaryFooterData;
  showPoweredByBeehiiv: boolean;
  shouldRenderOverContent: boolean;
}

const ProprietaryFooterCore = ({
  backgroundColor,
  textColor,
  data,
  showPoweredByBeehiiv,
  shouldRenderOverContent,
}: ProprietaryFooterCoreProps) => {
  // Calculate effective text color with contrast check
  const effectiveTextColor =
    textColor && hasMinimumContrast(textColor, backgroundColor) ? textColor : getContrastingColor(backgroundColor);

  const { translations, publicationName, privacyPolicyUrl, termsOfUseUrl } = data;

  const body = (
    <div className={container} style={{ backgroundColor: shouldRenderOverContent ? 'transparent' : backgroundColor }}>
      <div className={copyrightSection}>
        <span className={copyrightText} style={{ color: effectiveTextColor }}>
          {translations.copyrightText}
        </span>
      </div>

      <div className={linksSection}>
        <div className={policyLinks}>
          <PolicyLink
            href="https://www.beehiiv.com/report_abuse"
            text={translations.reportAbuse}
            color={effectiveTextColor}
            publicationName={publicationName}
          />
          <PolicyLink
            href={privacyPolicyUrl || 'https://beehiiv.com/privacy'}
            text={translations.privacyPolicy}
            color={effectiveTextColor}
            publicationName={publicationName}
          />
          <PolicyLink
            href={termsOfUseUrl || 'https://beehiiv.com/tou'}
            text={translations.termsOfUse}
            color={effectiveTextColor}
            publicationName={publicationName}
          />
        </div>

        {showPoweredByBeehiiv && (
          <PoweredByBadge
            publicationName={publicationName}
            text={translations.poweredByBeehiiv}
            color={effectiveTextColor}
          />
        )}
      </div>
    </div>
  );

  return shouldRenderOverContent ? <div className={absoluteWrapper}>{body}</div> : body;
};

/**
 * ProprietaryFooterElement - For use with Serializer (element-based rendering)
 */
export const ProprietaryFooterElement = ({
  element,
  showPoweredByBeehiiv = true,
  shouldRenderOverContent = false,
}: ProprietaryFooterElementProps) => {
  const { data } = element;
  const { backgroundColor, color } = element.attrs || {};

  if (!data?.showBeehiivBranding) return null;

  return (
    <ProprietaryFooterCore
      backgroundColor={backgroundColor}
      textColor={color}
      data={data}
      showPoweredByBeehiiv={showPoweredByBeehiiv}
      shouldRenderOverContent={shouldRenderOverContent}
    />
  );
};

/**
 * ProprietaryFooter - For direct usage (props-based rendering)
 * Use this in orchid-v2 and Dream Builder where data comes from context/hooks
 */
export const ProprietaryFooter = ({
  backgroundColor,
  textColor,
  data,
  showPoweredByBeehiiv = true,
  shouldRenderOverContent = false,
}: ProprietaryFooterProps) => {
  if (!data?.showBeehiivBranding) return null;

  return (
    <ProprietaryFooterCore
      backgroundColor={backgroundColor}
      textColor={textColor}
      data={data}
      showPoweredByBeehiiv={showPoweredByBeehiiv}
      shouldRenderOverContent={shouldRenderOverContent}
    />
  );
};
